import { useMemo } from 'react';
import { useUser } from '@/queries/useUser';
import { filterMenuItems } from '@/lib/utils/menuUtils';
import { AFFILIATE_MENUITEMS, ALL_MENU_ITEMS, MENUITEMS } from '@/lib/constants';
import { SUBSCRIPTION_TIERS } from '@/lib/api/subscriptions/types';

export function useNavigationItems() {
    const { user } = useUser();
    return useMemo(() => {
        const isAffiliateOnlySubscription = user?.subscription_tier === SUBSCRIPTION_TIERS.AFFILIATE;

        // Affiliate-only users get limited menu items
        if (isAffiliateOnlySubscription) {
            const { appItems, accountItems } = filterMenuItems(user ?? null, AFFILIATE_MENUITEMS);

            // For affiliate users, sidebar shows AFFILIATE and INCOME
            const sidebarItems = appItems.filter(item => item.id !== 'settings');
            // Bottom menu shows only SETTINGS
            const bottomMenuItems = accountItems.filter(item => item.id === 'settings');

            return {
                sidebarItems,
                bottomMenuItems,
                pinnableItems: [], // No pinnable items for affiliate-only users
                adminItems: [],
                accountItems,
                mobileSecondaryItems: [], // No secondary items for affiliate-only users
                isAffiliateOnlySubscription: true,
            };
        }

        const { appItems: allAppItems, adminItems, accountItems } = filterMenuItems(user ?? null, ALL_MENU_ITEMS);

        const fixedItemIds = new Set(MENUITEMS.top.fixed.map(item => item.id));
        const optionalItemIds = new Set(MENUITEMS.top.optional.map(item => item.id));
        const pinnedApiIds = new Set((user?.data?.pinned_navigation ?? []).map(item => item.id));

        const fixedItems = allAppItems.filter(item => fixedItemIds.has(item.id));
        const pinnableItems = allAppItems.filter(item => optionalItemIds.has(item.id));
        const pinnedOptionalItems = pinnableItems.filter(item => pinnedApiIds.has(item.id));
        const pinnedAdminItems = adminItems.filter(item => pinnedApiIds.has(item.id));
        const mobileSecondaryItems = MENUITEMS.mobile.secondary;

        const sidebarItems = [...fixedItems, ...pinnedOptionalItems, ...pinnedAdminItems].sort(
            (a, b) =>
                (ALL_MENU_ITEMS.findIndex(item => item.id === a.id) || 0) -
                (ALL_MENU_ITEMS.findIndex(item => item.id === b.id) || 0)
        );

        const bottomMenuItems = accountItems.filter(item => MENUITEMS.bottom.fixed.some(b => b.id === item.id));

        return {
            sidebarItems,
            bottomMenuItems,
            pinnableItems,
            adminItems,
            accountItems,
            mobileSecondaryItems,
            isAffiliateOnlySubscription: false,
        };
    }, [user]);
}
